import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { colors } from 'src/theme'
import { useDispatch } from 'react-redux'
import { MCLiteAssignToModal } from './MCLiteAssignToModal'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import UnassignImage from 'src/assets/images/unassign.svg'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UnassignSubcontractorToMCLite } from 'src/constants/actionTypes'
import { RelationContractor } from 'src/interfaces/mcLite'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { MCLiteUnassignToModal } from './MCLiteUnassignToModal'

interface Props {
  contractorId: string
  currentTemplateId: string
  className?: any
  isNotAllowed?: boolean
  assignedTo?: RelationContractor | null
  onRefresh: () => void | Promise<void>
}

const MCLiteAssignToDataRowComponent = ({
  contractorId,
  currentTemplateId,
  className,
  assignedTo,
  isNotAllowed,
  onRefresh,
}: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const {
    isOpen: isAssignModalOpen,
    open: openAssignModal,
    close: closeAssignModal,
  } = useModalOpen()
  const {
    isOpen: isUnassignModalOpen,
    open: openUnassignModal,
    close: closeUnassignModal,
  } = useModalOpen()
  const {
    isOpen: isUnassignConfirmModalOpen,
    open: openUnassignConfirmModal,
    close: closeUnassignConfirmModal,
  } = useModalOpen()

  const [newTemplateId, setNewTemplateId] = React.useState<string | undefined>(undefined)

  const handleCloseConfirmUnassign = useCallback(() => {
    setNewTemplateId(undefined)
    closeUnassignConfirmModal()
  }, [])

  const handleUnassign = useCallback(async () => {
    try {
      const templateId = newTemplateId === currentTemplateId ? undefined : newTemplateId

      await createAsyncAction(
        dispatch,
        UnassignSubcontractorToMCLite.request({
          subcontractorId: contractorId,
          mcLiteId: assignedTo?.id,
          templateId,
        }),
      )

      onRefresh()

      showToast('Contractor unassigned.', { variant: 'success' })
    } catch (err: any) {
      handleApiError(err)
    }

    handleCloseConfirmUnassign()
  }, [contractorId, assignedTo?.id, newTemplateId, currentTemplateId, onRefresh])

  const handleConfirmUnassign = useCallback((templateId: string) => {
    setNewTemplateId(templateId)
    closeUnassignModal()
    openUnassignConfirmModal()
  }, [])

  return (
    <div
      css={[
        tw`py-[14px] border-0 border-t border-solid border-[#EDEDED] flex items-center justify-between`,
        className,
      ]}
    >
      <div css={[tw`flex w-full flex-col gap-1.5 max-w-[204px]`]}>
        <Typography color={colors.SHADES_GREY_400} variant="h6">
          Assigned To
        </Typography>
        <Typography style={{ wordWrap: 'break-word' }} css={tw`text-black`} variant="body2">
          {assignedTo?.name || 'None'}
        </Typography>
      </div>

      <div css={tw`flex gap-2 flex-row ml-4`}>
        {!!assignedTo && hasPermission(UserPermission.McLiteDelete) && (
          <Button
            color="grey"
            size="small"
            leftIcon={<GSIcon iconName="gs_circle_x_1" />}
            className={tw`min-w-[36px]`}
            onClick={openUnassignModal}
          />
        )}
        {hasPermission(UserPermission.McLiteEdit) && (
          <Tooltip
            disableHoverListener={!isNotAllowed}
            title={
              <Fragment>
                <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                  Assignment Not Allowed
                </Typography>
                <Typography variant="body2" color={colors.SHADES_GREY_300}>
                  This contractor already has subcontractors assigned and is considered a Master
                  Contractor. They cannot be assigned as a subcontractor to another contractor. To
                  assign this contractor, please unassign all subcontractors first.
                </Typography>
              </Fragment>
            }
            color="black"
          >
            <span>
              <Button
                color="grey"
                size="small"
                disabled={isNotAllowed}
                leftIcon={<GSIcon iconName="gs_pencil" />}
                className={tw`min-w-[36px]`}
                onClick={openAssignModal}
              />
            </span>
          </Tooltip>
        )}
      </div>
      <MCLiteAssignToModal
        contractorId={contractorId}
        isOpen={isAssignModalOpen}
        isReassign={!!assignedTo}
        onClose={closeAssignModal}
        onSubmit={onRefresh}
      />
      <MCLiteUnassignToModal
        currentTemplateId={currentTemplateId}
        isOpen={isUnassignModalOpen}
        onClose={closeUnassignModal}
        onSubmit={handleConfirmUnassign}
      />
      <SensitiveActionModal
        isOpen={isUnassignConfirmModalOpen}
        submitLabel="Unassign"
        title="Unassign Contractor"
        text="Are you sure you want to unassign Contractor?"
        image={<img src={UnassignImage} />}
        onSubmit={handleUnassign}
        onClose={handleCloseConfirmUnassign}
      />
    </div>
  )
}

export const MCLiteAssignToDataRow = memo(MCLiteAssignToDataRowComponent)
